// src/app/work-order-numbers/components/work-order-import/work-order-import.component.ts
import { Component, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom, take } from 'rxjs';
import { PlanningTopNavComponent } from '../../../../shared/planning-nav/planning-top-nav.component';
import { WorkOrderFileParserService } from './services/work-order-file-parser.service';
import { WorkOrderUploadService, WorkOrderNumber } from './services/work-order-upload.service';
import { WorkOrderImportRow } from './models/work-order-import-row.model';

// If you already have these shared models, import them instead.
export interface AggregatedError {
  field: string;
  values: string[];
}

export interface UploadRowSummary {
  rowIndex: number;
  employeeNumber: string; // kept for compatibility with existing modal styles
  status: 'success' | 'partial' | 'fail';
  errors: string[];
  warnings: string[];
}

export interface UploadLog {
  uploadedBy: string;
  timestamp: Date;
  durationMs: number;
  totalRows: number;
  successCount: number;
  errors: { rowIndex: number; message: string }[];
  warnings: { rowIndex: number; message: string }[];
  summary: UploadRowSummary[];

  createdWorkOrders?: WorkOrderNumber[];
}

@Component({
  selector: 'app-work-order-import',
  standalone: true,
  imports: [CommonModule, PlanningTopNavComponent],
  templateUrl: './work-order-import.component.html',
  styleUrls: ['./work-order-import.component.scss'],
})
export class WorkOrderImportComponent {
  selectedFile: File | null = null;

  logs: UploadLog[] = [];
  aggregatedErrors: AggregatedError[] = [];
  activeLog: UploadLog | null = null;

  isLoading = false;
  errorMessage: string | null = null;

  constructor(
    private readonly parser: WorkOrderFileParserService,
    private readonly uploadService: WorkOrderUploadService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.reset();
    this.selectedFile = input.files[0];
    input.value = '';
  }

  handleDrop(event: DragEvent) {
    event.preventDefault();
    if (!event.dataTransfer?.files?.length) return;
    this.reset();
    this.selectedFile = event.dataTransfer.files[0];
  }

  removeFile() {
    this.reset();
  }

  private reset() {
    this.selectedFile = null;
    this.errorMessage = null;
    this.aggregatedErrors = [];
    this.activeLog = null;
  }

  async upload() {
    if (!this.selectedFile) return;

    this.isLoading = true;
    this.errorMessage = null;

    const startTime = performance.now();
    this.cdr.detectChanges();

    try {
      // 1) Parse file
      const rows: WorkOrderImportRow[] = await lastValueFrom(
        this.parser.parse(this.selectedFile).pipe(take(1))
      );
      const totalRows = rows.length;

      if (!totalRows) throw new Error('No data rows found in file');

      // 2) Upload
      const createdWorkOrders = await lastValueFrom(
        this.uploadService.uploadRows(rows).pipe(take(1))
      );
      const createdCount = createdWorkOrders.length;

      // 3) Current user
      const user = await lastValueFrom(this.uploadService.getCurrentUser().pipe(take(1)));
      const uploadedBy = `${user.lastName}, ${user.firstName} (${user.id})`;

      // 4) Result summary
      const resultValues: string[] = [];
      resultValues.push(`Imported ${createdCount}/${totalRows}.`);

      if (createdCount === 0) {
        resultValues.push('No records were created. This is commonly caused by duplicates.');
      } else if (createdCount < totalRows) {
        resultValues.push(`${totalRows - createdCount} row(s) were not imported (commonly duplicates).`);
      }

      this.aggregatedErrors = [{ field: 'Upload Result', values: resultValues }];

      // 5) Log
      const newLog: UploadLog = {
        uploadedBy,
        timestamp: new Date(),
        durationMs: Math.round(performance.now() - startTime),
        totalRows,
        successCount: createdCount,
        errors: [],
        warnings: [],
        summary: [],
        createdWorkOrders,
      };

      this.logs = [newLog, ...this.logs];
      this.activeLog = newLog;

      this.cdr.detectChanges();
    } catch (err: any) {
      this.errorMessage = err?.error?.message ?? err?.message ?? 'Upload failed';
      this.cdr.detectChanges();
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }
}